import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { HiStar } from 'react-icons/hi2';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Button from '../../components/Button';
import {
  useMultiGate,
  useMultiGateReferenceSessions,
  useSetMultiGateGates,
  useSetMultiGateReference,
  useSyncMultiGate,
} from '../../hooks/useSessions';
import { useRoutesForManagement } from '../../hooks/useRoutes';
import { useStoredState } from '../../hooks/useStoredState';
import { buildGateOptions, comparisonGateIds, GateOption } from '../../utils/multiGate';

function formatSynced(ts: string | null): string {
  if (!ts) return 'never synced';
  return `synced ${new Date(ts).toLocaleString()}`;
}

/**
 * Advanced → Multi-gate.
 *
 * Pick a reference session, then tick the gates to compare it on: the reference's own gate plus any
 * starred gate in the team. Each ticked gate gets a comparison session that mirrors the reference
 * (configs, config values, copies of the runs) with that gate. Synchronise re-mirrors the reference
 * onto every comparison and reprocesses the comparison runs. Calibration always comes from the reference.
 * "Set as Reference" swaps a comparison's gate onto the reference session, which re-calibrates on it.
 */
export default function MultiGate(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedId, setStoredId] = useStoredState<string>('multiGate_selectedSessionId', '');
  const [selectedId, setSelectedId] = useState<string>(() => searchParams.get('session') ?? storedId);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmReferenceGate, setConfirmReferenceGate] = useState<string | null>(null);

  const { data: referenceSessions, isPending: isSessionsPending } = useMultiGateReferenceSessions();
  const { data: group, isFetching: isGroupFetching, isError: isGroupError } = useMultiGate(selectedId);
  const { data: routes, isPending: isRoutesPending } = useRoutesForManagement();
  const setGates = useSetMultiGateGates();
  const syncGroup = useSyncMultiGate();
  const setReference = useSetMultiGateReference();

  const selectSession = (sessionId: string) => {
    setSelectedId(sessionId);
    setStoredId(sessionId);
    setSearchParams(sessionId ? { session: sessionId } : {}, { replace: true });
  };

  // Opened on a comparison session: switch to its reference.
  useEffect(() => {
    if (group && group.reference.id !== selectedId) selectSession(group.reference.id);
  }, [group]);

  const savedGateIds = useMemo(() => (group ? comparisonGateIds(group) : []), [group]);
  useEffect(() => {
    setChecked(new Set(savedGateIds));
    setConfirmRemove(false);
    setConfirmReferenceGate(null);
  }, [savedGateIds]);

  const options: GateOption[] = useMemo(
    () => (group ? buildGateOptions(routes ?? [], group) : []),
    [routes, group],
  );

  const toAdd = [...checked].filter((id) => !savedGateIds.includes(id));
  const toRemove = savedGateIds.filter((id) => !checked.has(id));
  const isDirty = toAdd.length > 0 || toRemove.length > 0;

  const toggle = (gateId: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(gateId)) next.delete(gateId);
      else next.add(gateId);
      return next;
    });
    setConfirmRemove(false);
  };

  const save = async () => {
    if (!group) return;
    if (toRemove.length > 0 && !confirmRemove) {
      setConfirmRemove(true);
      return;
    }
    try {
      const result = await setGates.mutateAsync({ sessionId: group.reference.id, gatesIds: [...checked] });
      const added = result.added?.length ?? 0;
      const removed = result.removed?.length ?? 0;
      toast.success(`Comparison sessions: ${added} added, ${removed} removed`);
    } catch {
      toast.error('Failed to update the comparison gates');
    }
  };

  // First click arms the row, second click swaps.
  const makeReference = async (gateId: string) => {
    if (!group) return;
    if (confirmReferenceGate !== gateId) {
      setConfirmReferenceGate(gateId);
      return;
    }
    try {
      const result = await setReference.mutateAsync({ sessionId: group.reference.id, gatesId: gateId });
      toast.success(`Reference gate changed — ${result.reprocess_queued ?? 0} reference runs reprocessing; `
        + 'comparisons re-sync once autocal finishes');
    } catch {
      toast.error('Failed to change the reference gate');
    }
    setConfirmReferenceGate(null);
  };

  const synchronise = async () => {
    if (!group) return;
    try {
      const result = await syncGroup.mutateAsync(group.reference.id);
      toast.success(result.status === 'already_queued'
        ? 'A synchronise is already queued'
        : 'Synchronise queued — comparison runs will reprocess');
    } catch {
      toast.error('Failed to queue synchronise');
    }
  };

  return (
    <div className="cr-w-full cr-space-y-6 cr-p-6">
      <SectionHeader title="Multi-gate" isLoading={isGroupFetching || setGates.isPending} />

      <p className="cr-max-w-3xl cr-text-base cr-text-gray-700">
        Compare one session across several gate pairs. Each ticked gate gets a comparison session
        that mirrors the reference session: its configs, its config values and copies of its runs,
        with that gate instead. All calibration comes from the reference, fitted on the reference
        gate. Edits made directly on a comparison session are overwritten by Synchronise. Set as
        Reference makes a comparison&apos;s gate the reference gate: the reference session keeps its
        runs, takes that gate and re-calibrates on it, and the comparisons then follow.
      </p>

      <div>
        <label htmlFor="multigate-reference" className="cr-mb-2 cr-block cr-text-base cr-font-medium cr-text-blue-900">
          Reference session
        </label>
        <select
          id="multigate-reference"
          value={selectedId}
          onChange={(e) => selectSession(e.target.value)}
          disabled={isSessionsPending}
          className={'cr-w-full cr-rounded-lg cr-border cr-border-gray-300 cr-p-3 cr-text-base cr-text-blue-900 '
            + 'focus:cr-border-blue-500 focus:cr-ring-2 focus:cr-ring-blue-500'}
        >
          <option value="">Select a reference session...</option>
          {referenceSessions?.map((session: any) => (
            <option key={session.id} value={session.id}>
              {session.date ?? ''} - {session.description}
            </option>
          ))}
        </select>
      </div>

      {selectedId && isGroupError && (
        <p className="cr-text-red-700">Could not load this session&apos;s multi-gate group.</p>
      )}

      {group && (
        <div className="cr-space-y-4">
          <div className="cr-text-base cr-text-blue-900">
            Reference:{' '}
            <Link to={`/sessions/${group.reference.id}`} className="cr-font-semibold cr-underline">
              {group.reference.description}
            </Link>
            {!group.reference.gate && (
              <span className="cr-ml-2 cr-text-amber-700">(this session has no gate yet)</span>
            )}
          </div>

          {isRoutesPending ? <Spinner isFetching /> : (
            <GateTable
              options={options}
              checked={checked}
              onToggle={toggle}
              confirmReferenceGate={confirmReferenceGate}
              referenceDisabled={isDirty || setReference.isPending || !group.reference.gate}
              onSetReference={makeReference}
            />
          )}

          {options.length <= 1 && !isRoutesPending && (
            <p className="cr-text-gray-600">
              No starred gates yet. Star gates under Advanced → Routes to offer them here.
            </p>
          )}

          {confirmReferenceGate && (
            <p className="cr-rounded-md cr-border cr-border-amber-300 cr-bg-amber-50 cr-p-3 cr-text-amber-900">
              The reference session will take this gate and its current gate moves to that comparison.
              The reference&apos;s runs reprocess and autocal re-fits its calibration on the new gate;
              the comparisons re-sync when that finishes. Press Confirm to go ahead.
            </p>
          )}

          {confirmRemove && toRemove.length > 0 && (
            <p className="cr-rounded-md cr-border cr-border-red-300 cr-bg-red-50 cr-p-3 cr-text-red-800">
              Saving deletes {toRemove.length} comparison session{toRemove.length > 1 ? 's' : ''} and
              their copied runs. The reference session is not touched. Press Save again to confirm.
            </p>
          )}

          <div className="cr-flex cr-flex-wrap cr-gap-3">
            <Button
              variant={confirmRemove ? 'danger' : 'outline'}
              size="compact"
              onClick={save}
              disabled={!isDirty || setGates.isPending}
            >
              {confirmRemove ? 'Confirm and save' : 'Save'}
            </Button>
            <Button
              variant="outline"
              size="compact"
              onClick={synchronise}
              disabled={isDirty || group.comparisons.length === 0 || syncGroup.isPending}
            >
              Synchronise
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface GateTableProps {
  options: GateOption[];
  checked: Set<string>;
  onToggle: (gateId: string) => void;
  /** The gate whose Set as Reference has been clicked once and is waiting for Confirm. */
  confirmReferenceGate: string | null;
  referenceDisabled: boolean;
  onSetReference: (gateId: string) => void;
}

function GateTable({
  options, checked, onToggle, confirmReferenceGate, referenceDisabled, onSetReference,
}: GateTableProps): ReactElement {
  return (
    <div className="cr-overflow-x-auto">
      <table className="cr-w-full cr-text-left cr-text-base">
        <thead className="cr-border-b cr-border-gray-300 cr-text-blue-900">
          <tr>
            <th className="cr-w-10 cr-p-2" aria-label="Selected" />
            <th className="cr-p-2">Gate</th>
            <th className="cr-p-2">Route</th>
            <th className="cr-p-2">Distance</th>
            <th className="cr-p-2">Comparison session</th>
          </tr>
        </thead>
        <tbody>
          {options.map((option) => (
            <tr key={option.id} className="cr-border-b cr-border-gray-100">
              <td className="cr-p-2">
                <input
                  type="checkbox"
                  aria-label={`Compare on ${option.label}`}
                  checked={option.isReference || checked.has(option.id)}
                  disabled={option.isReference}
                  onChange={() => onToggle(option.id)}
                />
              </td>
              <td className="cr-p-2">
                <span className="cr-inline-flex cr-items-center cr-gap-1">
                  {option.pinned && <HiStar className="cr-text-amber-500" aria-label="Starred" />}
                  {option.label}
                </span>
              </td>
              <td className="cr-p-2">{option.routeDescription}</td>
              <td className="cr-p-2">
                {option.routeDistance != null ? `${Math.round(option.routeDistance)} m` : '—'}
              </td>
              <td className="cr-p-2">
                {option.isReference && <span className="cr-font-semibold cr-text-blue-900">Reference</span>}
                {option.comparison && (
                  <span className="cr-flex cr-flex-wrap cr-items-center cr-gap-x-3 cr-gap-y-1">
                    <Link to={`/sessions/${option.comparison.id}`} className="cr-underline">
                      {option.comparison.run_count} runs
                    </Link>
                    <span className="cr-text-sm cr-text-gray-500">
                      {formatSynced(option.comparison.multigate_synced_ts)}
                    </span>
                    <Button
                      variant={confirmReferenceGate === option.id ? 'danger' : 'ghost'}
                      size="sm"
                      normalCase
                      onClick={() => onSetReference(option.id)}
                      disabled={referenceDisabled}
                    >
                      {confirmReferenceGate === option.id ? 'Confirm' : 'Set as Reference'}
                    </Button>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
